import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { ApiPaths, api } from "@/lib/api";
import { useHasPermission } from "@/lib/permissions";
import { roleLabel } from "@/lib/roleLabels";
import type { Permission, Role } from "@/lib/types";

export function RoleEditor() {
  const { t } = useTranslation();
  // Without role.update the editor is read-only — checkboxes disabled +
  // no Save (the backend 403s a write anyway; this stops the misleading
  // editable-looking UI for view-only roles like operator).
  const canEdit = useHasPermission("role.update");
  const roles = useQuery({
    queryKey: ["roles"],
    queryFn: async () => (await api.get<Role[]>(ApiPaths.orgRoles)).data,
  });
  const perms = useQuery({
    queryKey: ["permissions-catalog"],
    queryFn: async () => (await api.get<Permission[]>(ApiPaths.orgPermissions)).data,
  });

  const [activeId, setActiveId] = useState<number | null>(null);
  const role = roles.data?.find((r) => r.id === activeId) ?? roles.data?.[0] ?? null;

  return (
    <section className="grid gap-6 md:grid-cols-[16rem_1fr]">
      <aside>
        <h2 className="mb-2 text-lg font-semibold">{t("admin.roles.sectionTitle")}</h2>
        <ul className="rounded-xl border border-nb-border bg-nb-surface/40">
          {(roles.data ?? []).map((r) => (
            <li key={r.id}>
              <button
                className={`flex w-full items-center justify-between px-3 py-2 text-left text-sm ${
                  role?.id === r.id ? "bg-nb-surface text-nb-mint" : "text-nb-text"
                }`}
                onClick={() => setActiveId(r.id)}
              >
                {roleLabel(t, r.slug, r.name)}
                {r.is_system && (
                  <span className="text-xs text-nb-text-3">
                    {t("admin.roles.systemBadge")}
                  </span>
                )}
              </button>
            </li>
          ))}
        </ul>
      </aside>

      <div>
        {role ? (
          // `key` forces a fresh RoleDetail (and fresh `granted` state)
          // per role — without it React reuses the instance across role
          // switches, so the previously-selected role's edited permission
          // set bleeds onto the next role you save. That was the "editing
          // one role changes them all" bug.
          <RoleDetail
            key={role.id}
            role={role}
            permissions={perms.data ?? []}
            canEdit={canEdit}
          />
        ) : (
          <p className="text-sm text-nb-text-2">{t("admin.roles.selectRole")}</p>
        )}
      </div>
    </section>
  );
}

function RoleDetail({
  role,
  permissions,
  canEdit,
}: {
  role: Role;
  permissions: Permission[];
  canEdit: boolean;
}) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [granted, setGranted] = useState<string[]>(role.permissions);

  const save = useMutation({
    mutationFn: async () => {
      const res = await api.patch<Role>(ApiPaths.orgRole(role.id), { permissions: granted });
      return res.data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["roles"] });
      void qc.invalidateQueries({ queryKey: ["me"] });
    },
  });

  const grouped = groupBy(permissions, (p) => p.category || "other");

  return (
    <div className="space-y-4">
      <header className="flex items-baseline justify-between">
        <div>
          <h3 className="text-lg font-semibold">{roleLabel(t, role.slug, role.name)}</h3>
          <p className="text-sm text-nb-text-2">
            {t(`roleDesc.${role.slug}`, { defaultValue: role.description || "" }) ||
              t("admin.roles.noDescription")}
          </p>
          {role.is_system && (
            <p className="mt-1 text-xs text-cvp-warn">
              {t("admin.roles.systemRoleNotice")}
            </p>
          )}
          {!canEdit && (
            <p className="mt-1 text-xs text-nb-text-3">
              {t(
                "admin.roles.readOnlyNotice",
                "View only — you don't have permission to edit roles.",
              )}
            </p>
          )}
        </div>
        {canEdit && (
          <button className="btn-primary" disabled={save.isPending} onClick={() => save.mutate()}>
            {save.isPending ? t("admin.roles.saving") : t("admin.roles.saveChanges")}
          </button>
        )}
      </header>

      <div className="space-y-4">
        {Object.entries(grouped).map(([category, perms]) => (
          <fieldset key={category} className="card border-nb-border">
            <legend className="px-2 text-xs uppercase tracking-wide text-nb-text-2">
              {category}
            </legend>
            <ul className="space-y-2">
              {perms.map((p) => {
                const on = granted.includes(p.slug);
                return (
                  <li key={p.slug} className="flex items-start gap-3">
                    <input
                      id={`perm-${role.id}-${p.slug}`}
                      type="checkbox"
                      checked={on}
                      disabled={!canEdit}
                      onChange={() =>
                        setGranted((prev) =>
                          on ? prev.filter((s) => s !== p.slug) : [...prev, p.slug],
                        )
                      }
                      className="mt-1 h-4 w-4 rounded border-slate-600 bg-nb-surface text-nb-mint focus:ring-nb-mint"
                    />
                    <label htmlFor={`perm-${role.id}-${p.slug}`} className="text-sm">
                      <code className="text-nb-mint">{p.slug}</code>
                      <span className="ml-2 text-xs text-nb-text-2">
                        {t(`permDesc.${p.slug.replaceAll(".", "_")}`, {
                          defaultValue: p.description,
                        })}
                      </span>
                    </label>
                  </li>
                );
              })}
            </ul>
          </fieldset>
        ))}
      </div>
    </div>
  );
}

function groupBy<T>(items: T[], key: (t: T) => string): Record<string, T[]> {
  const out: Record<string, T[]> = {};
  for (const it of items) {
    const k = key(it);
    (out[k] ??= []).push(it);
  }
  return out;
}
